/**
 * AAAprinters - Products React Hook
 * Fetches products from Supabase database with images
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ProductImage {
  url: string;
  position: number;
  alt_text?: string;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  sale_price?: number;
  description?: string;
  short_description?: string;
  category?: string;
  featured?: boolean;
  badge?: string;
  rating?: number;
  images: ProductImage[];
}

export interface UseProductsReturn {
  products: Product[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching products from database
 */
export function useProducts(): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use single nested query for better performance
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          id,
          name,
          price,
          sale_price,
          description,
          short_description,
          slug,
          featured,
          stock_status,
          rating_average,
          product_images (
            url,
            position,
            alt_text
          )
        `)
        .eq('stock_status', 'instock')
        .order('created_at', { ascending: false });

      if (productsError) {
        console.error('Products query error:', productsError);
        throw productsError;
      }

      if (!productsData || productsData.length === 0) {
        setProducts([]);
        return;
      }

      // Transform data
      const transformedProducts: Product[] = productsData.map((p: any) => ({
        id: p.id,
        name: p.name,
        price: p.price,
        sale_price: p.sale_price,
        description: p.description || p.short_description || '',
        category: 'General', // Default category since column doesn't exist
        featured: p.featured || false,
        badge: p.featured ? 'Featured' : undefined,
        rating: p.rating_average || 4.5,
        images: (p.product_images || [])
          .sort((a: any, b: any) => a.position - b.position)
          .map((img: any) => ({
            url: img.url,
            position: img.position,
            alt_text: img.alt_text,
          })),
      }));

      setProducts(transformedProducts);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch products');
      setError(error);
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return {
    products,
    loading,
    error,
    refetch: fetchProducts,
  };
}

/**
 * Hook for fetching featured products
 */
export function useFeaturedProducts(): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('products')
        .select(`
          id,
          name,
          price,
          sale_price,
          description,
          short_description,
          slug,
          featured,
          stock_status,
          rating_average,
          product_images (
            url,
            position,
            alt_text
          )
        `)
        .eq('stock_status', 'instock')
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(8);

      if (fetchError) {
        console.error('Supabase query error:', fetchError);
        throw fetchError;
      }

      const transformedProducts: Product[] = (data || []).map(p => ({
        id: p.id,
        name: p.name,
        price: p.price,
        sale_price: p.sale_price,
        description: p.description || p.short_description || '',
        category: 'General', // Default category since column doesn't exist
        featured: true,
        badge: 'Featured',
        rating: p.rating_average || 4.5,
        images: (p.product_images || [])
          .sort((a: any, b: any) => a.position - b.position)
          .map((img: any) => ({
            url: img.url,
            position: img.position,
            alt_text: img.alt_text,
          })),
      }));

      setProducts(transformedProducts);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch featured products');
      setError(error);
      console.error('Error fetching featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return {
    products,
    loading,
    error,
    refetch: fetchProducts,
  };
}
